import { DateTime } from "luxon";
import _ from "lodash";

import { UPDATE_RESOURCE } from "@/env";

import Connection from "@/js/network/helpers";
import { getScope } from "@/js/auth/auth-config";

import icons from "@/assets/icons.json";
import store from "@/store/store";
import { manageAPIError } from "@/js/api/builder/api-error";
import { listStoreModulesWithMethod } from "@/store/helpers/store-helpers";
import { customMapping, routerPillProps } from "@/js/utils/changelog";

import { icon as lifecycleIcon } from "@/js/plugins/lifecycle";

import type {
	ApiChangelogEntry,
	ApiChangelogPageResponse,
	ChangelogChange,
	ChangelogComment,
	ChangelogEntry,
	ChangelogPageResult,
	ChangelogPageState,
	ChangelogPrimitive,
	ChangelogRepresentation,
	ChangelogSourceConfig,
	ChangelogSourceDescriptor,
	ChangelogTitle,
	ChangelogValue,
	RawChangelogChange,
	RawChangelogComment,
	RawChangelogEntry
} from "@/js/api/types/changelog";
import type { Uuid } from "@/js/api/types";

const lodash = _ as typeof _ & {
	keysToCamel: <T>(value: T) => T;
};

const idFieldRegex = /(?<fieldName>.*)(?<idAppend>Ids|Id)$|systems/;

const DEFAULT_CHANGELOG_PAGE_SIZE = 50;

type ChangelogRepresentationFunction = (
	value: unknown,
	otherValue?: Record<string, unknown>
) => unknown;

type ChangelogExtractFieldMapping = {
	newFields: string[];
	fallback: unknown;
};

type ChangelogCustomMapping = {
	fieldNames?: Record<string, string>;
	representationFunctions?: Record<string, ChangelogRepresentationFunction>;
	extractFields?: Record<string, ChangelogExtractFieldMapping>;
	disableChangeEqualMerging?: Record<string, boolean>;
};

const typedCustomMapping = customMapping as Record<
	string,
	ChangelogCustomMapping
>;

type InternalChangelogChange = Omit<
	ChangelogChange,
	"oldValue" | "newValue"
> & {
	oldValue?: unknown;
	newValue?: unknown;
};

type InternalChangelogEntry = Omit<
	ChangelogEntry,
	"changes" | "lifecycleChange"
> & {
	changes: InternalChangelogChange[];
	lifecycleChange?: InternalChangelogChange | null;
};

const enforceArray = <T>(value: T | T[]): T[] => {
	return Array.isArray(value) ? value : [value];
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
	return value != null && typeof value === "object" && !Array.isArray(value);
};

const isRepresentation = (
	value: unknown
): value is ChangelogRepresentation & Record<string, unknown> => {
	return isRecord(value) && typeof value.representationComponent === "string";
};

const parseComment = (comment: RawChangelogComment): ChangelogComment => ({
	...comment,
	timestamp: DateTime.fromISO(comment.timestamp, { zone: "utc" })
});

const parseChangelog = (
	unparsedEntry: ApiChangelogEntry
): InternalChangelogEntry => {
	const entry = lodash.keysToCamel(
		unparsedEntry
	) as unknown as RawChangelogEntry;
	const commentRevisions = (entry.commentRevisions ?? []).map(parseComment);

	return {
		...entry,
		timestamp: DateTime.fromISO(entry.timestamp, { zone: "utc" }),
		comment: parseComment(entry.comment),
		commentRevisions,
		objectType: entry.objectType ?? ""
	} as InternalChangelogEntry;
};

const normalizeArraysAndObjects =
	(objectType: string) =>
	(entry: InternalChangelogEntry): InternalChangelogEntry => {
		if (objectType.includes("specification")) {
			return entry;
		}

		entry.changes = entry.changes
			.map((change) => {
				const { oldValue, newValue } = change;

				if (Array.isArray(newValue) || Array.isArray(oldValue)) {
					const oldValueArray = Array.isArray(oldValue) ? oldValue : [];
					const newValueArray = Array.isArray(newValue) ? newValue : [];
					change.isArrayChange = true;

					const disableChangeEqualMerging =
						typedCustomMapping[objectType]?.disableChangeEqualMerging?.[
							change.field
						];
					if (!disableChangeEqualMerging) {
						change.oldValue = _.differenceWith(
							oldValueArray,
							newValueArray,
							_.isEqual
						);
						change.newValue = _.differenceWith(
							newValueArray,
							oldValueArray,
							_.isEqual
						);
					}

					return change;
				}

				if (isRecord(oldValue) || isRecord(newValue)) {
					change.oldValue = oldValue ?? {};
					change.newValue = newValue ?? {};
					change.isObjectChange = true;

					Object.keys(
						change.oldValue as unknown as Record<string, unknown>
					).forEach((key) => {
						const oldObject = change.oldValue as unknown as Record<
							string,
							unknown
						>;
						const newObject = change.newValue as unknown as Record<
							string,
							unknown
						>;
						if (oldObject[key] === newObject[key]) {
							delete oldObject[key];
							delete newObject[key];
						}
					});
				}

				return change;
			})
			.filter(({ oldValue, newValue }) => {
				if (Array.isArray(newValue) && Array.isArray(oldValue)) {
					return Boolean(newValue.length || oldValue.length);
				}

				return (oldValue ?? newValue) != null;
			});

		return entry;
	};

const findChangeIdRepresentation =
	(moduleName: string) =>
	(change: unknown): ChangelogValue | null => {
		if (!change) {
			return null;
		}

		const changeObject = store.getters[`${moduleName}/find`](change) as {
			name?: string;
		} | null;

		if (!changeObject) {
			return change as ChangelogPrimitive;
		}

		const props = routerPillProps(moduleName.slice(0, -1), changeObject);

		if (props) {
			return { representationComponent: "RouterPill", props };
		}

		return changeObject.name ?? null;
	};

const mapIdToName =
	(objectType: string) =>
	(entry: InternalChangelogEntry): InternalChangelogEntry => {
		const idToNameModules = listStoreModulesWithMethod(
			"getters",
			"find"
		) as string[];
		if (objectType.toLowerCase() === "reservationwindows") {
			return entry;
		}
		entry.changes = entry.changes.map((change) => {
			const { oldValue, newValue } = change;
			const idFieldMatch = change.field.match(idFieldRegex);

			if (idFieldMatch) {
				const fieldName = idFieldMatch.groups?.fieldName;
				const moduleName = fieldName
					? `${fieldName}s`
					: (idFieldMatch.input ?? "");

				if (!moduleName) {
					return change;
				}

				if (idToNameModules.includes(moduleName)) {
					const representationChange = (value: unknown) => {
						if (Array.isArray(value)) {
							return value.map(findChangeIdRepresentation(moduleName));
						}

						return [findChangeIdRepresentation(moduleName)(value)];
					};

					if (oldValue != null) {
						change.oldValue = representationChange(oldValue).filter(
							(value): value is ChangelogValue => value != null
						);
					}

					if (newValue != null) {
						change.newValue = representationChange(newValue).filter(
							(value): value is ChangelogValue => value != null
						);
					}
				}
			}

			return change;
		});

		return entry;
	};

const extractEntryInformation = (
	entry: InternalChangelogEntry
): InternalChangelogEntry => {
	entry.lifecycleChange =
		entry.changes.find(({ field }) => field === "lifecycleState") ?? null;

	return entry;
};

const applyCustomRepresentation =
	(objectType: string) =>
	(entry: InternalChangelogEntry): InternalChangelogEntry => {
		entry.changes = entry.changes.map((change) => {
			const { field, oldValue, newValue } = change;
			const representationFunctions =
				typedCustomMapping[objectType]?.representationFunctions?.[field];

			if (representationFunctions) {
				if (oldValue != null) {
					change.oldValue = representationFunctions(oldValue, {
						newValue
					});
				}

				if (newValue != null) {
					change.newValue = representationFunctions(newValue, {
						oldValue
					});
				}

				const isComponentChange = [
					...enforceArray(change.oldValue),
					...enforceArray(change.newValue)
				].some(isRepresentation);

				if (isComponentChange) {
					delete change.isObjectChange;
				}
			}

			return change;
		});

		return entry;
	};

const idFieldName = (field: string) => {
	const match = field.match(idFieldRegex);

	if (!match) {
		return field;
	}

	const fieldName = match.groups?.fieldName ?? "";
	const idAppend = match.groups?.idAppend;
	return `${fieldName}${idAppend === "Ids" ? "s" : ""}`;
};

const setCustomFieldNames =
	(objectType: string) =>
	(entry: InternalChangelogEntry): InternalChangelogEntry => {
		entry.changes = entry.changes.map((change) => ({
			...change,
			field: _.startCase(
				typedCustomMapping[objectType]?.fieldNames?.[change.field] ||
					idFieldName(change.field) ||
					change.field
			),
			originalField: change.field
		}));

		return entry;
	};

const transformCustomFields =
	(objectType: string) =>
	(entry: InternalChangelogEntry): InternalChangelogEntry => {
		const extractFields = typedCustomMapping[objectType]?.extractFields;

		if (extractFields) {
			Object.entries(extractFields).forEach(([originalField, mapping]) => {
				const index = entry.changes.findIndex(
					(change) => change.field === originalField
				);
				if (index < 0) {
					return;
				}

				const oldEntry = entry.changes[index];
				const oldValues = isRecord(oldEntry.oldValue) ? oldEntry.oldValue : {};
				const newValues = isRecord(oldEntry.newValue) ? oldEntry.newValue : {};
				mapping.newFields.forEach((newField: string) => {
					entry.changes.push({
						field: newField,
						oldValue: oldValues[newField] || mapping.fallback,
						newValue: newValues[newField] || mapping.fallback
					});
				});

				entry.changes.splice(index, 1);
			});
		}

		return entry;
	};

const getSimpleChange = (changes: InternalChangelogChange[]) => {
	const maxTitleLength = 23;
	if (changes.length !== 1) {
		return false;
	}
	const change = changes[0];

	const newValue =
		Array.isArray(change.newValue) && change.newValue.length === 1
			? change.newValue[0]
			: change.newValue;
	const oldValue =
		Array.isArray(change.oldValue) && change.oldValue.length === 1
			? change.oldValue[0]
			: change.oldValue;

	if (isRepresentation(newValue) || isRepresentation(oldValue)) {
		if (
			newValue?.representationValue != null ||
			oldValue?.representationValue != null
		) {
			return {
				newValue: newValue?.representationValue,
				oldValue: oldValue?.representationValue
			};
		}

		return false;
	}
	if (isRecord(newValue) || isRecord(oldValue)) {
		return false;
	}
	if (Array.isArray(newValue) && newValue.length > maxTitleLength) {
		return false;
	}

	return {
		newValue,
		oldValue
	};
};

const hasChangelogValue = (value: unknown) => {
	if (value == null) {
		return false;
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	if (isRecord(value)) {
		return Object.keys(value).length > 0;
	}

	return true;
};

const isCreatedWithoutLifecycle = (
	changes: InternalChangelogChange[],
	objectType: string
) => {
	const isWholeObjectCreation =
		objectType === "licenses" &&
		changes.length === 1 &&
		changes[0].isObjectChange;

	return (
		(changes.length > 1 || isWholeObjectCreation) &&
		changes.every((change) => !hasChangelogValue(change.oldValue)) &&
		changes.some((change) => hasChangelogValue(change.newValue))
	);
};

const generateTitle = (
	{ changes, lifecycleChange }: InternalChangelogEntry,
	objectType: string
): ChangelogTitle => {
	if (lifecycleChange) {
		if (lifecycleChange.oldValue == null) {
			return {
				...lifecycleChange,
				type: "created",
				icon: "plus"
			};
		}

		return {
			...lifecycleChange,
			type: "lifecycle",
			field: lifecycleChange.field,
			oldValue: Array.isArray(lifecycleChange.oldValue)
				? lifecycleChange.oldValue[0]
				: (lifecycleChange.oldValue as ChangelogValue | undefined),
			newValue: Array.isArray(lifecycleChange.newValue)
				? lifecycleChange.newValue[0]
				: (lifecycleChange.newValue as ChangelogValue | undefined),
			icon: lifecycleIcon(
				String(
					Array.isArray(lifecycleChange.newValue)
						? (lifecycleChange.newValue[0] ?? "")
						: (lifecycleChange.newValue ?? "")
				)
			)
		};
	}

	if (objectType.includes("specification")) {
		return {
			type: "specification"
		};
	}

	if (isCreatedWithoutLifecycle(changes, objectType)) {
		return {
			type: "created",
			icon: "plus"
		};
	}

	const simpleChange = getSimpleChange(changes);
	if (simpleChange) {
		const change = changes[0];

		return {
			...change,
			...simpleChange,
			type: "simple"
		};
	}

	if (changes.length === 1) {
		const change = changes[0];

		const newValueLength = Array.isArray(change.newValue)
			? change.newValue.length
			: 0;
		const oldValueLength = Array.isArray(change.oldValue)
			? change.oldValue.length
			: 0;
		const removed = !newValueLength && Boolean(oldValueLength);
		const added = Boolean(newValueLength) && !oldValueLength;
		const action = removed ? "Removed" : added ? "Added" : "Updated";

		const match = change.originalField?.match(idFieldRegex);
		const fieldName = match?.groups?.fieldName;
		const icon =
			fieldName && typeof icons[fieldName as keyof typeof icons] === "string"
				? fieldName
				: null;

		return {
			...change,
			type: "single",
			action,
			icon
		};
	}

	return { type: "default" };
};

const generateEntryTitle =
	(objectType: string) =>
	(entry: InternalChangelogEntry): InternalChangelogEntry => {
		entry.title = generateTitle(entry, objectType);

		return entry;
	};

const arrayFromChangeObject = (
	change: InternalChangelogChange
): RawChangelogChange[] => {
	const combinedKeys = Object.keys(
		Object.assign(
			{},
			(change.oldValue ?? {}) as Record<string, unknown>,
			(change.newValue ?? {}) as Record<string, unknown>
		)
	);

	return combinedKeys.map((key) => ({
		field: key,
		oldValue: (change.oldValue as unknown as Record<string, unknown>)[key],
		newValue: (change.newValue as unknown as Record<string, unknown>)[key]
	}));
};

const handleObjectChanges = (
	entry: InternalChangelogEntry
): InternalChangelogEntry => {
	entry.changes = entry.changes.reduce<InternalChangelogChange[]>(
		(carried, change) => {
			const changeList = change.isObjectChange
				? arrayFromChangeObject(change)
				: [change];
			return carried.concat(changeList as InternalChangelogChange[]);
		},
		[]
	);

	return entry;
};

const sortChanges = <T extends { changes: Array<{ field: string }> }>(
	entry: T
): T => {
	entry.changes.sort((a, b) => a.field.localeCompare(b.field));
	return entry;
};

const mapChangeToArray = (entry: InternalChangelogEntry): ChangelogEntry => {
	const normalizedChanges = entry.changes.map<ChangelogChange>((change) => {
		const { oldValue, newValue } = change;

		return {
			...change,
			oldValue:
				oldValue == null
					? []
					: enforceArray(oldValue as ChangelogValue | ChangelogValue[]),
			newValue:
				newValue == null
					? []
					: enforceArray(newValue as ChangelogValue | ChangelogValue[])
		};
	});

	return {
		...entry,
		changes: normalizedChanges
	} as ChangelogEntry;
};

export const sortEntries = (a: ChangelogEntry, b: ChangelogEntry) => {
	const diff = b.timestamp.toMillis() - a.timestamp.toMillis();
	if (diff) {
		return diff;
	}

	return (
		Number(Boolean(b.lifecycleChange)) - Number(Boolean(a.lifecycleChange))
	);
};

const mapChangelogEntries = (
	entries: ApiChangelogEntry[],
	objectType: string
): ChangelogEntry[] => {
	return entries
		.map(parseChangelog)
		.map((entry) => ({ ...entry, objectType }))
		.map(transformCustomFields(objectType))
		.map(normalizeArraysAndObjects(objectType))
		.map(mapIdToName(objectType))
		.map(extractEntryInformation)
		.map(applyCustomRepresentation(objectType))
		.map(setCustomFieldNames(objectType))
		.map(generateEntryTitle(objectType))
		.map(handleObjectChanges)
		.map(mapChangeToArray)
		.filter((entry) => entry.changes.length)
		.map(sortChanges);
};

const fetchConnection = new Connection("/24.08", await getScope("kogs")).axios;

const defaultChangelogPath = (id: Uuid) => `/${id}/changelogs`;

const buildPageParams = (
	cursor: string | null | undefined,
	pageSize: number
) => {
	const params = new URLSearchParams();
	params.set("page[size]", String(pageSize));

	if (cursor) {
		params.set("page[cursor]", cursor);
	}

	return params;
};

const mapSourceEntries = (
	source: ChangelogSourceDescriptor,
	entries: ApiChangelogEntry[]
) =>
	mapChangelogEntries(entries, source.objectType).map((entry) => ({
		...entry,
		specification: source.specification || entry.specification
	}));

const normalizeSourceId = (source: ChangelogSourceDescriptor) =>
	source.sourceId;

const fetchSourcePage = async (
	source: ChangelogSourceDescriptor,
	cursor: string | null | undefined,
	pageSize = DEFAULT_CHANGELOG_PAGE_SIZE
) => {
	try {
		const page = (
			await source.connection.get<ApiChangelogPageResponse>(source.path, {
				params: buildPageParams(cursor, pageSize)
			})
		).data;
		const entries = mapSourceEntries(source, page.data);
		const nextCursor =
			page.page?.cursor == null ? null : String(page.page.cursor);

		return {
			sourceId: source.sourceId,
			entries,
			nextCursor,
			exhausted: !nextCursor
		};
	} catch (error) {
		const responseError = error as { response?: { status?: number } };
		if (responseError.response?.status === 404) {
			return {
				sourceId: source.sourceId,
				entries: [],
				nextCursor: null,
				exhausted: true
			};
		}

		manageAPIError(error);
		throw error;
	}
};

export const mergeChangelogEntries = (
	existingEntries: ChangelogEntry[],
	incomingEntries: ChangelogEntry[]
) => {
	const entryMap = new Map<string, ChangelogEntry>();

	[...existingEntries, ...incomingEntries].forEach((entry) => {
		entryMap.set(entry.id, entry);
	});

	return Array.from(entryMap.values()).sort(sortEntries);
};

export const fetchChangelogPage = async (
	sources: ChangelogSourceDescriptor[],
	state: ChangelogPageState = {},
	pageSize = DEFAULT_CHANGELOG_PAGE_SIZE
): Promise<ChangelogPageResult> => {
	const exhausted = state.exhausted ?? {};
	const cursors = state.cursors ?? {};
	const activeSources = sources.filter(
		(source) => exhausted[normalizeSourceId(source)] !== true
	);

	if (!activeSources.length) {
		return {
			entries: [],
			cursors: {},
			exhausted: {}
		};
	}

	const sourcePages = await Promise.all(
		activeSources.map((source) =>
			fetchSourcePage(source, cursors[normalizeSourceId(source)], pageSize)
		)
	);

	return {
		entries: mergeChangelogEntries(
			[],
			sourcePages.flatMap(({ entries }) => entries)
		),
		cursors: sourcePages.reduce<Record<string, string | null>>(
			(carried, page) => {
				carried[page.sourceId] = page.nextCursor;
				return carried;
			},
			{}
		),
		exhausted: sourcePages.reduce<Record<string, boolean>>((carried, page) => {
			carried[page.sourceId] = page.exhausted;
			return carried;
		}, {})
	};
};

export const createStoreChangelogSources = (
	connections: ChangelogSourceConfig[],
	id: Uuid
): ChangelogSourceDescriptor[] => {
	return connections.map(
		({ connection, objectType, changelogPath, sourceId }) => ({
			sourceId: sourceId?.(id) ?? objectType,
			objectType,
			path: changelogPath?.(id) ?? defaultChangelogPath(id),
			connection: connection.axios
		})
	);
};

export const createManualChangelogSource = (
	objectType: string,
	id: Uuid
): ChangelogSourceDescriptor => ({
	sourceId: objectType,
	objectType,
	path: `${objectType}/${id}/changelogs`,
	connection: fetchConnection
});

export const createSpecificationChangelogSource = (
	objectType: string,
	objectId: Uuid,
	specificationId: Uuid
): ChangelogSourceDescriptor => {
	const pathPrefix = objectType === "systems" ? "systems/antennas" : objectType;

	return {
		sourceId: `${objectType}-specification:${specificationId}`,
		objectType: `${objectType}-specification`,
		path: `${pathPrefix}/${objectId}/specs/${specificationId}/changelogs`,
		connection: fetchConnection,
		specification: true
	};
};

const normalizeConnections = (
	connections: ChangelogSourceConfig | ChangelogSourceConfig[]
) => {
	return Array.isArray(connections) ? connections : [connections];
};

export default (
	connections: ChangelogSourceConfig | ChangelogSourceConfig[]
) => {
	const normalizedConnections = normalizeConnections(connections);

	return {
		getChangelogSources(_context: unknown, id: Uuid) {
			return createStoreChangelogSources(normalizedConnections, id);
		},
		watchChangelogs(
			_context: unknown,
			callback: (event: { id: Uuid }) => void
		) {
			normalizedConnections.forEach(({ connection }) =>
				connection.socket.on(UPDATE_RESOURCE, callback)
			);
		},
		unwatchChangelogs(
			_context: unknown,
			callback: (event: { id: Uuid }) => void
		) {
			normalizedConnections.forEach(({ connection }) =>
				connection.socket.off(UPDATE_RESOURCE, callback)
			);
		}
	};
};

export const fetchSingleChangelog = (id: Uuid, objectType: string) =>
	fetchConnection
		.get<ApiChangelogEntry>(`/changelogs/${id}`)
		.then((response) => {
			const mappedEntry = mapChangelogEntries([response.data], objectType);
			return {
				...mappedEntry[0],
				specification:
					objectType.includes("specification") || mappedEntry[0]?.specification
			};
		})
		.catch((error) => {
			manageAPIError(error);
			throw error;
		});

export { DEFAULT_CHANGELOG_PAGE_SIZE };
